import type { CaptureRow } from '../../data/database.types';
import { CAPTURE_STATUS_LABEL, formatCaptureTitle } from '../captures/capture-status';
import { formatInstant } from '../../lib/timezone';

/**
 * One selectable Capture in the report builder. M10 / S10-01.
 *
 * **A REAL CHECKBOX INSIDE A LABEL, AND THAT WAS A CORRECTION.** The first plan
 * proposed a `<button aria-pressed>`, on the grounds that Crewlink contains no
 * checkboxes anywhere. That was an argument from absence: checkboxes are absent
 * because the product had never had a multi-select list, not because they were
 * considered and rejected. `aria-pressed` describes a toggle button — bold in a
 * toolbar — while `checked` describes an item chosen from a set, which is
 * exactly what this is. The label makes the whole row tappable, so the design
 * export's card visual is unchanged, and keyboard support arrives for free
 * rather than being hand-built.
 *
 * **THE INPUT IS VISUALLY HIDDEN, NOT REMOVED.** `display: none` would take it
 * out of the accessibility tree and the tab order together; it is clipped
 * instead, so it is still focusable, still announced, and still the thing the
 * tests assert on. The visible ✓ is `aria-hidden` decoration for the eye.
 *
 * **SELECTION NEVER DEPENDS ON COLOUR ALONE** (PT §21, and the design export
 * states it for the status chips). The check mark is the primary signal; the
 * border and fill support it.
 *
 * **NOT SHOWN, DELIBERATELY:** the transcript body, which makes a selection
 * list slow to scan at phone width, and the photo counts, which are per-Capture
 * reads today and would cost one query per row for context that arrives free in
 * S10-02. **No UUID or internal id is rendered** — the Capture number is the
 * human identifier (PT §17: nothing internal reaches a client-facing surface,
 * and the habit starts here).
 *
 * **THE STATUS CHIP IS SHOWN ON PURPOSE.** The design export omits it, because
 * that export assumed the list was pre-filtered to REPAIRED Captures. D-034
 * removed the pre-filter — Brandon selects, and OPEN, REPAIRED and CLOSED are
 * all selectable — so from the moment every status can appear, hiding the
 * status would leave him unable to see what he is including.
 */
export function ReportCaptureRow({
  capture,
  locationDisplay,
  timeZone,
  selected,
  onToggle,
}: {
  readonly capture: CaptureRow;
  /** The ACCEPTED location, or null. Never invented (PT §5). */
  readonly locationDisplay: string | null;
  /** The canonical Crewlink timezone, or null while the profile is unread. */
  readonly timeZone: string | null;
  readonly selected: boolean;
  readonly onToggle: (captureId: string) => void;
}) {
  return (
    <li className="report-capture-item">
      <label className={`report-capture-row${selected ? ' report-capture-row--on' : ''}`}>
        <input
          type="checkbox"
          className="report-capture-input"
          checked={selected}
          onChange={() => {
            onToggle(capture.id);
          }}
        />
        {/* Decoration. The checkbox above carries the meaning; this carries the
            look, and hiding it from assistive technology stops the state being
            announced twice. */}
        <span className="report-capture-box" aria-hidden="true">
          {selected ? '✓' : ''}
        </span>
        <span className="report-capture-text">
          <span className="report-capture-title">
            {formatCaptureTitle(capture.capture_number, locationDisplay)}
          </span>
          <span className="report-capture-meta">
            {/* A profile with no canonical timezone renders no date rather than
                a guessed one — the same choice the Captures list makes. */}
            {timeZone ? formatInstant(capture.created_at, timeZone) : ''}
            <span className={`status-chip status-chip--capture-${capture.status}`}>
              {CAPTURE_STATUS_LABEL[capture.status]}
            </span>
          </span>
        </span>
      </label>
    </li>
  );
}
